import React, { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { useAuth } from "utils/useAuth/useAuth";
const GlobalOptionsCacheContext = createContext(null);
const DEFAULT_GLOBAL_OPTIONS_CACHE_KEY = "__no-session-scope__";

// Treat a full page of results without a total as "possibly more".
export const resolveHasMoreOptions = (total, optionsLength, pageSize) => {
  const normalizedTotal = Number(total);

  return Number.isFinite(normalizedTotal)
    ? normalizedTotal > optionsLength
    : optionsLength === pageSize;
};

// Plain React-owned cache provider. Cache lifetime follows this provider instance.
export const GlobalOptionsCacheProvider = ({ children }) => {
  const cacheRef = useRef(new Map());

  const fetchWithGlobalOptionsCache = useCallback(
    async ({ namespace, cacheKey, fetcher }) => {
      let namespaceCache = cacheRef.current.get(namespace);
      if (!namespaceCache) {
        namespaceCache = new Map();
        cacheRef.current.set(namespace, namespaceCache);
      }

      const cachedEntry = namespaceCache.get(cacheKey);

      if (
        cachedEntry &&
        Object.prototype.hasOwnProperty.call(cachedEntry, "data")
      ) {
        return cachedEntry.data;
      }

      if (cachedEntry?.promise) {
        return cachedEntry.promise;
      }

      const request = Promise.resolve()
        .then(fetcher)
        .then((data) => {
          namespaceCache.set(cacheKey, { data });
          return data;
        })
        .catch((error) => {
          namespaceCache.delete(cacheKey);
          throw error;
        });

      namespaceCache.set(cacheKey, { promise: request });

      return request;
    },
    [],
  );

  const invalidateGlobalOptionsCache = useCallback((namespace) => {
    if (!namespace) {
      cacheRef.current.clear();
      return;
    }

    cacheRef.current.delete(namespace);
  }, []);

  const value = useMemo(
    () => ({
      fetchWithGlobalOptionsCache,
      invalidateGlobalOptionsCache,
    }),
    [fetchWithGlobalOptionsCache, invalidateGlobalOptionsCache],
  );

  return (
    <GlobalOptionsCacheContext.Provider value={value}>
      {children}
    </GlobalOptionsCacheContext.Provider>
  );
};

// Remounts the cache provider whenever the logged-in user identity changes.
export const AuthScopedGlobalOptionsCacheProvider = ({ children }) => {
  const { user } = useAuth();
  const cacheScopeKey = user?.isoAgentId || DEFAULT_GLOBAL_OPTIONS_CACHE_KEY;

  return (
    <GlobalOptionsCacheProvider key={cacheScopeKey}>
      {children}
    </GlobalOptionsCacheProvider>
  );
};

// Access shared cache operations from components and cache hooks.
export const useGlobalOptionsCache = () => {
  const value = useContext(GlobalOptionsCacheContext);

  if (!value) {
    throw new Error(
      "useGlobalOptionsCache must be used within GlobalOptionsCacheProvider",
    );
  }

  return value;
};

// Builds a small options-cache hook around one namespace/fetcher pair.
export const createOptionsCacheHook = ({
  namespace,
  buildCacheKey,
  fetcher,
  fetchProperty = "fetchOptions",
  invalidateProperty = "invalidateOptionsCache",
}) => {
  return () => {
    const { fetchWithGlobalOptionsCache, invalidateGlobalOptionsCache } =
      useGlobalOptionsCache();

    const fetchOptions = useCallback(
      async (params) => {
        return fetchWithGlobalOptionsCache({
          namespace,
          cacheKey: buildCacheKey(params),
          fetcher: () => fetcher(params),
        });
      },
      [fetchWithGlobalOptionsCache],
    );

    const invalidateOptionsCache = useCallback(() => {
      invalidateGlobalOptionsCache(namespace);
    }, [invalidateGlobalOptionsCache]);

    return useMemo(
      () => ({
        [fetchProperty]: fetchOptions,
        [invalidateProperty]: invalidateOptionsCache,
      }),
      [fetchOptions, invalidateOptionsCache],
    );
  };
};
